import React, { createContext, useContext } from 'react';
import { ICellProps } from '@dealroadshow/uikit-table/DataTable';
import { IDataroomListItem } from '../domain/vo/DataroomListItem';
import { useCountryOfBusinessContext } from '@dataroom/country-of-business/CountryOfBusinessContext';
import { useDataroomTenantContext } from '@dataroom/tenant/DataroomTenantContext';
import config from '@dataroom/config';

const useDataroomAvailability = (countryId?: string | null) => {
  const { tenant } = useDataroomTenantContext();

  const isDisabled = (cellProps: ICellProps<IDataroomListItem>) => {
    const {
      isCountryOfBusinessEnabled,
    } = config[tenant].options;

    return isCountryOfBusinessEnabled && cellProps.row.restrictedCountryIds?.includes(countryId!);
  };

  return {
    isDisabled,
  };
};

export type DataroomAvailabilityContextType = ReturnType<typeof useDataroomAvailability>;

export const DataroomAvailabilityContext = createContext<DataroomAvailabilityContextType | null>(null);

export const useDataroomAvailabilityContext = () => {
  const context = useContext(DataroomAvailabilityContext);
  if (!context) {
    throw new Error('useDataroomAvailabilityContext must be used within a DataroomAvailabilityContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const CountryOfBusinessRestrictionWrapper = ({ children }: IProps) => {
  const { countryId } = useCountryOfBusinessContext();

  return (
    <DataroomAvailabilityContext.Provider value={ useDataroomAvailability(countryId) }>
      { children }
    </DataroomAvailabilityContext.Provider>
  );
};

const DataroomAvailabilityContextWrapper = ({ children }: IProps) => (
  <DataroomAvailabilityContext.Provider value={ useDataroomAvailability() }>
    { children }
  </DataroomAvailabilityContext.Provider>
);

const DataroomAvailabilityContextProvider = ({ children }: IProps) => {
  const { tenant } = useDataroomTenantContext();
  const {
    isCountryOfBusinessEnabled,
  } = config[tenant].options;

  const DataroomAvailabilityWrapper = isCountryOfBusinessEnabled
    ? CountryOfBusinessRestrictionWrapper
    : DataroomAvailabilityContextWrapper;

  return (
    <DataroomAvailabilityWrapper>
      { children }
    </DataroomAvailabilityWrapper>
  );
};

export default DataroomAvailabilityContextProvider;
